import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import EditIcon from "@mui/icons-material/EditOutlined";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FormikProps, FormikValues } from "formik";

import { useDebouncedState } from "../../../../../../hooks/useDebouncedState";
import { formatParticipantAddress } from "../../../../../../utils/participantAddress";
import {
  ProceedingParticipantResponse,
  ProceedingParticipantSearchResult,
  ProceedingParticipantType,
} from "../../../../../api/ClientFileApi";
import { useParticipantsApi } from "../../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../../query/clientFileQueryKeys";
import { ClientSpecificParticipantDialog } from "../../../client-specific-participant/ClientSpecificParticipantDialog";

const SearchDebounceMs = 300;

interface Props<T extends FormikValues> {
  formik: FormikProps<T>;
  /** Form field holding the selected participant id — `creditorId` or `representativeId`. */
  participantIdField: keyof T & string;
  /** Only participants of these types are offered (and creatable) in this field. */
  types: ProceedingParticipantType[];
  initialDisplayName?: string;
  initialParticipantId?: string;
  initialIsClientSpecific?: boolean;
  label: string;
  /**
   * The noun for what this field holds ("Gläubiger", "Vertreter"), interpolated into the search,
   * empty-state, create and edit copy. Required, so the field can never announce itself as the
   * wrong kind of participant.
   */
  entityLabel: string;
  required?: boolean;
  disabled?: boolean;
  inactiveError?: string;
  clientId?: string;
  onEditClientSpecific?: (participantId: string) => void;
}

export function ClaimParticipantSearch<T extends FormikValues>({
  formik,
  participantIdField,
  types,
  initialDisplayName = "",
  initialParticipantId,
  initialIsClientSpecific = false,
  label,
  entityLabel,
  required,
  disabled,
  inactiveError,
  clientId,
  onEditClientSpecific,
}: Props<T>) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const participantsApi = useParticipantsApi();
  // Depend on a primitive, never on `types` itself: `initialOption` drives a render-phase state
  // sync below, so a new array reference from the caller would reset the field back to its initial
  // value on every parent render while formik kept the newly picked id.
  const fallbackType = types[0] ?? ProceedingParticipantType.Creditor;
  const initialOption = useMemo<ProceedingParticipantSearchResult | null>(
    () =>
      initialDisplayName && initialParticipantId
        ? {
            proceedingParticipantId: initialParticipantId,
            name: initialDisplayName,
            type: fallbackType,
            isClientSpecific: initialIsClientSpecific,
            street: null,
            houseNumber: null,
            zipCode: null,
            city: null,
            country: null,
            isActive: true,
            deactivatedAtUtc: null,
          }
        : null,
    [initialParticipantId, initialDisplayName, initialIsClientSpecific, fallbackType]
  );
  const {
    value: inputValue,
    debouncedValue: debouncedInputValue,
    setValue: setInputValue,
    setValueImmediate: setInputValueImmediate,
  } = useDebouncedState(initialDisplayName, SearchDebounceMs);
  const [selectedOption, setSelectedOption] = useState<ProceedingParticipantSearchResult | null>(initialOption);
  const [syncedOption, setSyncedOption] = useState(initialOption);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [highlightedOption, setHighlightedOption] = useState<ProceedingParticipantSearchResult | null>(null);

  if (initialOption !== syncedOption) {
    setSyncedOption(initialOption);
    if (initialOption) {
      setSelectedOption(initialOption);
      setInputValueImmediate(initialOption.name);
    }
  }

  const { data: searchData } = useQuery({
    queryKey: [ClientFileQueryKey.ProceedingParticipants, "search", debouncedInputValue, clientId, types],
    queryFn: () =>
      participantsApi.searchParticipants({
        searchText: debouncedInputValue || undefined,
        clientId,
        types,
      }),
  });
  const options = useMemo(() => searchData?.data ?? [], [searchData]);

  const selectedParticipantId = selectedOption?.proceedingParticipantId;
  const { data: selectedParticipant } = useQuery({
    queryKey: [ClientFileQueryKey.ProceedingParticipants, selectedParticipantId],
    queryFn: () => participantsApi.getParticipantById({ clientId, participantId: selectedParticipantId ?? "" }),
    enabled: !!selectedParticipantId,
  });
  const [syncedSelectedParticipant, setSyncedSelectedParticipant] = useState(selectedParticipant);

  if (selectedParticipant !== syncedSelectedParticipant) {
    setSyncedSelectedParticipant(selectedParticipant);
    if (selectedParticipant && selectedOption) {
      if (selectedParticipant.proceedingParticipantId === selectedOption.proceedingParticipantId) {
        const refreshed: ProceedingParticipantSearchResult = {
          proceedingParticipantId: selectedParticipant.proceedingParticipantId,
          name: selectedParticipant.name,
          type: selectedParticipant.type,
          street: selectedParticipant.street,
          houseNumber: selectedParticipant.houseNumber,
          zipCode: selectedParticipant.zipCode,
          city: selectedParticipant.city,
          country: selectedParticipant.country,
          isClientSpecific: !selectedParticipant.isGlobal,
          isActive: selectedParticipant.isActive,
          deactivatedAtUtc: selectedParticipant.deactivatedAtUtc ?? null,
        };
        const changed =
          refreshed.name !== selectedOption.name ||
          refreshed.street !== selectedOption.street ||
          refreshed.houseNumber !== selectedOption.houseNumber ||
          refreshed.zipCode !== selectedOption.zipCode ||
          refreshed.city !== selectedOption.city ||
          refreshed.isClientSpecific !== selectedOption.isClientSpecific ||
          refreshed.isActive !== selectedOption.isActive ||
          refreshed.deactivatedAtUtc !== selectedOption.deactivatedAtUtc;
        if (changed) {
          setSelectedOption(refreshed);
          setInputValueImmediate(refreshed.name);
        }
      }
    }
  }

  const participantIdError = formik.errors[participantIdField] as string | undefined;
  const showError =
    !!inactiveError || (!!participantIdError && (formik.touched[participantIdField] as boolean | undefined));
  const errorMessage = inactiveError ?? participantIdError;

  const handleCreated = (created: ProceedingParticipantResponse) => {
    const newOption: ProceedingParticipantSearchResult = {
      proceedingParticipantId: created.proceedingParticipantId,
      name: created.name,
      type: created.type,
      street: created.street,
      houseNumber: created.houseNumber,
      zipCode: created.zipCode,
      city: created.city,
      country: created.country,
      isClientSpecific: !created.isGlobal,
      isActive: created.isActive,
      deactivatedAtUtc: created.deactivatedAtUtc ?? null,
    };
    setSelectedOption(newOption);
    setInputValueImmediate(newOption.name);
    void formik.setFieldValue(participantIdField, newOption.proceedingParticipantId);
    void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ProceedingParticipants] });
  };

  return (
    <>
      <Autocomplete<ProceedingParticipantSearchResult>
        size="small"
        value={selectedOption}
        inputValue={inputValue}
        options={options}
        getOptionLabel={(option) => option.name}
        filterOptions={(x) => x}
        disabled={disabled}
        isOptionEqualToValue={(option, value) => option.proceedingParticipantId === value.proceedingParticipantId}
        onChange={(_, newValue) => {
          setSelectedOption(newValue);
          void formik.setFieldValue(participantIdField, newValue?.proceedingParticipantId ?? "");
          if (!newValue) {
            setInputValue("");
          }
        }}
        onHighlightChange={(_, option) => setHighlightedOption(option)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && inputValue && options.length === 0 && clientId) {
            e.preventDefault();
            setIsCreateDialogOpen(true);
            return;
          }
          if (e.key === "F2" && highlightedOption?.isClientSpecific && onEditClientSpecific) {
            e.preventDefault();
            onEditClientSpecific(highlightedOption.proceedingParticipantId);
          }
        }}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === "input" || reason === "reset") {
            setInputValue(newInputValue);
          }
          if (reason === "clear") {
            setInputValue("");
            setSelectedOption(null);
            void formik.setFieldValue(participantIdField, "");
          }
        }}
        renderInput={(params) => {
          const isSelectedClientSpecific =
            !!selectedOption?.isClientSpecific && !!selectedOption.proceedingParticipantId;
          return (
            <TextField
              {...params}
              label={label}
              required={required}
              error={showError}
              helperText={showError ? errorMessage : " "}
              onBlur={() => void formik.setFieldTouched(participantIdField, true)}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSelectedClientSpecific && (
                        <InputAdornment position="end">
                          <Chip
                            label={t("proceedingParticipants.clientSpecificBadge")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </InputAdornment>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          );
        }}
        renderOption={(props, option) => {
          const address = formatParticipantAddress(option.street, option.houseNumber, option.zipCode, option.city);
          return (
            <li {...props} key={option.proceedingParticipantId}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  {address && (
                    <Typography variant="caption" color="text.secondary">
                      {address}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  {option.isClientSpecific && (
                    <Chip
                      label={t("proceedingParticipants.clientSpecificBadge")}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {option.isClientSpecific && onEditClientSpecific && (
                    <IconButton
                      size="small"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditClientSpecific(option.proceedingParticipantId);
                      }}
                      aria-label={`${t("claims.form.editClientSpecificParticipant", { entity: entityLabel })} (F2)`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </li>
          );
        }}
        noOptionsText={
          inputValue ? (
            <Box>
              <Typography variant="body2">
                {clientId
                  ? t("claims.form.noParticipantResultsPressEnter", { query: inputValue, entity: entityLabel })
                  : t("claims.form.noParticipantResults", { query: inputValue })}
              </Typography>
              {clientId && (
                <Button
                  variant="contained"
                  size="small"
                  sx={{ mt: 1 }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setIsCreateDialogOpen(true)}
                  startIcon={<span aria-hidden>+</span>}
                  aria-label={t("claims.form.createClientSpecificParticipant", { entity: entityLabel })}
                >
                  {t("claims.form.createClientSpecificParticipant", { entity: entityLabel })}
                </Button>
              )}
            </Box>
          ) : (
            t("claims.form.participantSearch", { entity: entityLabel })
          )
        }
      />
      {isCreateDialogOpen && clientId && (
        <ClientSpecificParticipantDialog
          clientId={clientId}
          allowedTypes={types}
          prefilledName={inputValue}
          onCreated={handleCreated}
          onClose={() => setIsCreateDialogOpen(false)}
        />
      )}
    </>
  );
}
